import apiClient, { apiOrigin } from '@u/apiClient'
import { authorizeRequest } from '@u/http/authorizeRequest'
import { clearObservedUser } from '@/observability/faro'
import { redirectToLogin } from '@/router/utils/loginRedirect'
import ChatApprovalController from '@/wayfinder/actions/App/Http/Controllers/Ai/ChatApprovalController'
import ChatController from '@/wayfinder/actions/App/Http/Controllers/Ai/ChatController'

// The store cannot import `@/i18n` (it imports the store), so a failed send
// resolves to an error code and the conversation shows the translated toast.
// `message` is the API's own (already translated) message, when it sends one.
export type SendMessageErrorCode =
  | 'network'
  | 'forbidden'
  | 'invalid'
  | 'quota_exceeded'
  | 'rate_limited'
  | 'server'
  | 'interrupted'
  | 'provider'
  | 'conflict'
  | 'stale'

export interface SendMessageError {
  code: SendMessageErrorCode
  message?: string
}

// An action the assistant suggested on a resource: the back builds the prompt
// itself, `text` is only what the conversation shows as the user's message.
export interface SuggestedActionRequest {
  key: string
  resource_type: 'invoice'
  resource_id: string
}

export type SendMessagePayload =
  | string
  | { text: string; suggestedAction?: SuggestedActionRequest }

// The approval decisions no longer match the changes waiting on the back (a
// message or another tab moved on): it sends back the ones still waiting.
interface ApprovalConflict {
  code: 'conflict'
  pendingIds: string[]
}

// The request failed before the reply started: the back never got it.
const NOT_RECEIVED_CODES: SendMessageErrorCode[] = [
  'network',
  'forbidden',
  'invalid',
  'quota_exceeded',
  'rate_limited',
  'server'
]

/**
 * Posts to one of the chat routes and reads the reply it streams into the
 * conversation.
 */
async function streamReply(
  commit,
  route: { url: string; method: string },
  body: Record<string, unknown>
): Promise<SendMessageError | ApprovalConflict | undefined> {
  commit('setSending', true)

  // Native fetch, not apiClient: the answer is a server-sent event stream read chunk by chunk,
  // which the ofetch client cannot hand over. authorizeRequest still gives it the same headers
  // (bearer token, Faro session id) and sends an expired session back to the login page.
  const request = {
    headers: {
      'Content-Type': 'application/json',
      'X-Locale': apiClient.defaults.headers.common['X-Locale']
    } as Record<string, string>
  }
  authorizeRequest(request)
  if (!request.headers.Authorization) {
    commit('setSending', false)
    return
  }

  let response
  try {
    response = await fetch(`${apiOrigin}${route.url}`, {
      method: route.method.toUpperCase(),
      headers: request.headers,
      body: JSON.stringify(body)
    })
  } catch {
    commit('setSending', false)
    return { code: 'network' }
  }

  if (response.status === 401) {
    commit('setSending', false)
    clearObservedUser()
    redirectToLogin()
    return
  }
  if (response.status === 403) {
    commit('setSending', false)
    const data = await response.json().catch(() => null)
    return {
      code: 'forbidden',
      message: data?.errors?.permissions ?? data?.errors?.roles ?? data?.message
    }
  }
  if (response.status === 422) {
    commit('setSending', false)
    const data = await response.json().catch(() => null)
    return { code: 'invalid', message: data?.message }
  }
  if (response.status === 409) {
    commit('setSending', false)
    const data = await response.json().catch(() => null)
    // The suggested action no longer applies (the resource changed meanwhile)
    if (data?.code === 'SUGGESTED_ACTION_STALE') {
      return { code: 'stale', message: data.message }
    }
    return {
      code: 'conflict',
      pendingIds: (data?.approvals ?? []).map((approval) => approval.id)
    }
  }
  if (response.status === 429) {
    commit('setSending', false)
    const data = await response.json().catch(() => null)
    if (data?.code === 'AI_MONTHLY_QUOTA_EXCEEDED') {
      commit('setQuota', data.usage)
      return { code: 'quota_exceeded', message: data.message }
    }
    return { code: 'rate_limited' }
  }
  if (!response.ok) {
    commit('setSending', false)
    return { code: 'server' }
  }

  const reader = response.body.getReader()
  const decoder = new TextDecoder()
  let buffer = ''
  let assistantMessageId = null

  try {
    while (true) {
      const { done, value } = await reader.read()
      // The back always ends with [DONE], even after an error: a stream that
      // ends without it was cut on the way (proxy timeout, network).
      if (done) {
        return { code: 'interrupted' }
      }
      buffer += decoder.decode(value, { stream: true })
      const parts = buffer.split('\n\n')
      buffer = parts.pop()

      for (const part of parts) {
        if (!part.startsWith('data: ')) {
          continue
        }
        const raw = part.slice(6)
        if (raw === '[DONE]') {
          commit('setSending', false)
          return
        }
        let event
        try {
          event = JSON.parse(raw)
        } catch {
          continue
        }

        switch (event.type) {
          case 'text_delta':
            if (event.message_id !== assistantMessageId) {
              assistantMessageId = event.message_id
              commit('addMessage', {
                id: assistantMessageId,
                role: 'assistant',
                content: ''
              })
            }
            commit('appendToMessage', {
              id: assistantMessageId,
              delta: event.delta
            })
            break
          case 'tool_call':
            commit('addToolCall', {
              id: event.tool_id,
              name: event.tool_name
            })
            break
          case 'tool_result':
            commit('completeToolCall', event.tool_id)
            break
          case 'widget':
            commit('addWidget', {
              id: `w-${event.message_id}`,
              widgetType: event.widget_type,
              data: event.data
            })
            break
          // The reply waits for the user to approve or reject these changes.
          case 'tool_approval_request':
            for (const approval of event.approvals) {
              commit('addApproval', approval)
            }
            break
          case 'conversation':
            commit('setConversationId', event.conversation_id)
            break
          case 'ai_quota':
            commit('setQuota', event)
            break
          // The provider failed mid-reply: the back reports it and ends the stream.
          case 'error':
            return { code: 'provider' }
        }
      }
    }
  } catch {
    return { code: 'interrupted' }
  } finally {
    commit('settleRunningToolCalls')
    commit('setSending', false)
  }
}

export default {
  open(context) {
    context.commit('open')
  },
  close(context) {
    context.commit('close')
  },
  toggle(context) {
    context.commit('toggle')
  },
  setManualDocked(context, value) {
    context.commit('setManualDocked', value)
  },
  setDockedWidth(context, value) {
    context.commit('setDockedWidth', value)
  },
  async loadMockConversation({ commit }) {
    if (!import.meta.env.DEV) {
      return
    }
    const { mockConversation } = await import('./mockConversation')
    commit('setSending', false)
    commit('setMessages', structuredClone(mockConversation))
  },
  async sendMessage(
    { commit, state },
    payload: SendMessagePayload
  ): Promise<SendMessageError | undefined> {
    const { text, suggestedAction } =
      typeof payload === 'string' ? { text: payload } : payload
    // Moving on leaves the changes waiting for approval undone (the back answers them as not approved).
    commit('skipPendingApprovals')
    commit('addMessage', {
      id: `u-${Date.now()}`,
      role: 'user',
      content: text
    })

    // The chat route never answers with an approval conflict.
    return (await streamReply(commit, ChatController.post(), {
      message: text,
      conversation_id: state.conversationId,
      ...(suggestedAction && { suggested_action: suggestedAction })
    })) as SendMessageError | undefined
  },
  /**
   * Records the user's decision on one change, and resumes the reply once every
   * change of the same reply is decided.
   */
  async decideApproval(
    { commit, state },
    { id, approved }
  ): Promise<SendMessageError | undefined> {
    commit('setApprovalStatus', {
      id,
      status: approved ? 'approved' : 'rejected'
    })

    const approvals = state.messages.filter(
      (message) => message.role === 'approval' && !message.submitted
    )
    if (approvals.some((approval) => approval.status === 'pending')) {
      return
    }

    const decisions = Object.fromEntries(
      approvals.map((approval) => [approval.id, approval.status === 'approved'])
    )
    const ids = Object.keys(decisions)
    commit('markApprovalsSubmitted', ids)

    const result = await streamReply(commit, ChatApprovalController.post(), {
      conversation_id: state.conversationId,
      decisions
    })
    if (result && 'pendingIds' in result) {
      commit('restoreApprovals', { ids, pendingIds: result.pendingIds })
      return { code: 'conflict' }
    }
    // The changes still wait on the back: let the user decide them again. Once
    // the reply has started (interrupted, provider), what ran is unknown.
    if (result && NOT_RECEIVED_CODES.includes(result.code)) {
      commit('restoreApprovals', { ids, pendingIds: ids })
    }
    return result as SendMessageError | undefined
  }
}
